import React, { useEffect, useMemo, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  ArrowLeftIcon,
  CheckIcon,
  ExclamationTriangleIcon,
  ArrowPathIcon,
} from '@heroicons/react/24/outline';
import { useConnection } from '../../contexts/EnhancedConnectionContext';
import { useUI } from '../../contexts/UIContext';
import {
  useMonitor,
  useCreateMonitor,
  useUpdateMonitor,
} from '../../hooks/useMonitors';
import { metadataAPI } from '../../api/enhancedApiService';
import monitorsService from '../../services/monitorsService';

// Unified monitor create/edit form. Routes:
//   /monitors/:connectionSlug/new        → create
//   /monitors/:connectionSlug/:monitorId → edit
//
// Replaces both AnomalyConfigForm.js and the inline create dialog that
// lived in the old CustomMetricsPage. The form is one page rather than
// a wizard — every field is conditional on at most one parent choice
// (source_type or evaluation_mode), and a wizard adds back-button
// friction without adding clarity.
//
// Payload shape mirrors backend/core/monitors/models.py
// validate_monitor_payload(). Only the fields relevant to the selected
// source_type / evaluation_mode are sent; the rest are stripped at
// submit so the backend dataclasses don't reject unknown keys.

// Default empty state. Used for create mode and as the reset target when
// the user switches between source types / eval modes.
const EMPTY_FORM = {
  name: '',
  display_name: '',
  description: '',
  is_active: true,
  source_type: 'builtin_table_metric',
  source_config: {
    table_name: '',
    column_name: '',
    metric: 'row_count',
    query: '',
    // Default to 'value' so users can paste the placeholder example
    // and have it work without renaming anything. They can change it
    // if their query uses a different alias.
    value_column: 'value',
    segment_column: '',
    // How the dashboard collapses per-segment values into one overall
    // number on segmented custom_sql monitors (sum/avg/min/max). Default
    // 'sum' since the dominant pattern is SELECT segment, SUM/COUNT(...)
    // GROUP BY segment. Ignored for built-in metrics (engine computes
    // a true un-grouped value via a second query).
    aggregation_method: 'sum',
  },
  evaluation_mode: 'statistical',
  evaluation_config: {
    method: 'zscore',
    sensitivity: 1.0,
    baseline_window_days: 14,
    direction: 'below',
    warning: '',
    critical: '',
  },
  segment_by: '',
  collection_interval_minutes: 60,
  min_data_points: 3,
  // Schedule. Defaults match the legacy "every 60 minutes" behavior so a
  // form that's saved without touching the schedule section reproduces
  // the pre-2026-05-19 cadence.
  schedule_type: 'interval',
  schedule_config: {
    every: 60,
    unit: 'minutes',
    // Daily / weekly extras — populated when the user picks those types.
    // Browser TZ at first render seeds timezone so the form defaults to
    // the user's local time rather than UTC.
    hour: 9,
    minute: 0,
    day_of_week: 1, // Monday in JS/Postgres convention
    timezone:
      (typeof Intl !== 'undefined' &&
        Intl.DateTimeFormat().resolvedOptions().timeZone) || 'UTC',
  },
};

// Day-of-week labels in JS/Postgres convention (0=Sunday).
const DAY_OF_WEEK_LABELS = [
  { value: 0, label: 'Sunday' },
  { value: 1, label: 'Monday' },
  { value: 2, label: 'Tuesday' },
  { value: 3, label: 'Wednesday' },
  { value: 4, label: 'Thursday' },
  { value: 5, label: 'Friday' },
  { value: 6, label: 'Saturday' },
];

// Compact list of "well-known" IANA timezones we surface in the dropdown.
// Browser TZ is added at runtime if it's not already in this list, so
// users in less-common zones still see their own option pre-selected.
const COMMON_TIMEZONES = [
  'UTC',
  'America/New_York',
  'America/Chicago',
  'America/Denver',
  'America/Los_Angeles',
  'Europe/London',
  'Europe/Berlin',
  'Asia/Tokyo',
  'Asia/Singapore',
  'Australia/Sydney',
];

// Human labels for the source_type radio. Order matches the backend
// SOURCE_TYPES tuple so the default index lines up.
const SOURCE_OPTIONS = [
  {
    value: 'builtin_table_metric',
    label: 'Table metric (row count)',
    blurb: 'Track row count on a single table. Cheapest and most common monitor.',
  },
  {
    value: 'builtin_column_metric',
    label: 'Column metric',
    blurb: 'Null %, distinct count, min/max/avg/stddev — on a specific column.',
  },
  {
    value: 'custom_sql',
    label: 'Custom SQL',
    blurb:
      'Write your own query — we run it and read the metric from the column you specify.',
  },
];

const EVALUATION_OPTIONS = [
  {
    value: 'statistical',
    label: 'ML / Statistical',
    blurb:
      'Learn baseline from history and alert on z-score, IQR, or moving-average deviation.',
  },
  {
    value: 'threshold',
    label: 'Threshold',
    blurb: 'Manual warning + critical lines. Alert fires when the value crosses them.',
  },
];

const COLUMN_METRICS = [
  { value: 'null_pct', label: 'Null %' },
  { value: 'distinct_count', label: 'Distinct count' },
  { value: 'distinct_pct', label: 'Distinct %' },
  { value: 'min', label: 'Minimum' },
  { value: 'max', label: 'Maximum' },
  { value: 'avg', label: 'Average' },
  { value: 'stddev', label: 'Standard deviation' },
];

const STATISTICAL_METHODS = [
  { value: 'zscore', label: 'Z-score' },
  { value: 'iqr', label: 'IQR (Interquartile Range)' },
  { value: 'moving_average', label: 'Moving average' },
];

// Build the request payload from the local form state. Strips fields
// that don't apply to the current source_type / evaluation_mode so the
// backend dataclasses (which fail on unknown shape) only see what they
// expect. Returns either a payload or `{ error }` if a required field
// is missing — keeps the validation surface in one place rather than
// scattering required-asterisk logic across inputs.
const buildPayload = (form) => {
  if (!form.name || !form.name.trim()) {
    return { error: 'Name is required' };
  }

  let sourceConfig;
  switch (form.source_type) {
    case 'builtin_table_metric':
      if (!form.source_config.table_name) {
        return { error: 'Table is required for a table metric' };
      }
      sourceConfig = {
        table_name: form.source_config.table_name,
        metric: form.source_config.metric || 'row_count',
      };
      break;
    case 'builtin_column_metric':
      if (!form.source_config.table_name) {
        return { error: 'Table is required for a column metric' };
      }
      if (!form.source_config.column_name) {
        return { error: 'Column is required for a column metric' };
      }
      sourceConfig = {
        table_name: form.source_config.table_name,
        column_name: form.source_config.column_name,
        metric: form.source_config.metric || 'null_pct',
      };
      break;
    case 'custom_sql': {
      if (!form.source_config.query || !form.source_config.query.trim()) {
        return { error: 'SQL query is required' };
      }
      if (!form.source_config.value_column) {
        return { error: 'Value column is required for custom SQL' };
      }
      const cfg = {
        query: form.source_config.query,
        value_column: form.source_config.value_column,
        aggregation_method: form.source_config.aggregation_method || 'sum',
      };
      if (form.source_config.segment_column) {
        cfg.segment_column = form.source_config.segment_column;
      }
      sourceConfig = cfg;
      break;
    }
    default:
      return { error: `Unknown source_type: ${form.source_type}` };
  }

  // For segmented custom_sql, segment_column must be set in source_config.
  // The backend enforces this too, but catching it client-side gives a
  // cleaner error path.
  if (
    form.segment_by &&
    form.source_type === 'custom_sql' &&
    !form.source_config.segment_column
  ) {
    return {
      error:
        "Segmented custom SQL monitors require a Segment column in the SQL config.",
    };
  }

  let evaluationConfig;
  if (form.evaluation_mode === 'statistical') {
    const sens = Number(form.evaluation_config.sensitivity);
    if (!Number.isFinite(sens) || sens <= 0) {
      return { error: 'Sensitivity must be a positive number' };
    }
    evaluationConfig = {
      method: form.evaluation_config.method || 'zscore',
      sensitivity: sens,
      baseline_window_days: Number(form.evaluation_config.baseline_window_days) || 14,
    };
  } else if (form.evaluation_mode === 'threshold') {
    const warn = form.evaluation_config.warning;
    const crit = form.evaluation_config.critical;
    const warnNum =
      warn === '' || warn === null || warn === undefined ? null : Number(warn);
    const critNum =
      crit === '' || crit === null || crit === undefined ? null : Number(crit);
    if (warnNum === null && critNum === null) {
      return { error: 'Set at least one of Warning or Critical for a threshold monitor' };
    }
    if (warnNum !== null && !Number.isFinite(warnNum)) {
      return { error: 'Warning must be numeric' };
    }
    if (critNum !== null && !Number.isFinite(critNum)) {
      return { error: 'Critical must be numeric' };
    }
    // The backend also enforces this cross-field rule; checking here for
    // a faster failure with a clearer message.
    if (warnNum !== null && critNum !== null) {
      if (form.evaluation_config.direction === 'below' && critNum > warnNum) {
        return {
          error: 'For below thresholds, Critical must be ≤ Warning.',
        };
      }
      if (form.evaluation_config.direction === 'above' && critNum < warnNum) {
        return {
          error: 'For above thresholds, Critical must be ≥ Warning.',
        };
      }
    }
    evaluationConfig = {
      direction: form.evaluation_config.direction || 'below',
      warning: warnNum,
      critical: critNum,
    };
  } else {
    return { error: `Unknown evaluation_mode: ${form.evaluation_mode}` };
  }

  const minPts = Number(form.min_data_points);
  if (!Number.isInteger(minPts) || minPts < 1) {
    return { error: 'Minimum data points must be a positive integer' };
  }

  // Schedule. Build a clean schedule_config dict containing ONLY the
  // fields that apply to the selected type — the backend dataclasses
  // are strict about extras so we avoid sending hour/minute on an
  // interval schedule, etc.
  let scheduleConfig;
  let collectionIntervalMinutes = Number(form.collection_interval_minutes) || 60;
  switch (form.schedule_type) {
    case 'manual':
      scheduleConfig = {};
      // collection_interval_minutes is meaningless for manual but the
      // legacy column is non-null; carry it as the user's last known
      // interval so flipping back to 'interval' restores the cadence.
      break;
    case 'interval': {
      const every = Number(form.schedule_config.every);
      const unit = form.schedule_config.unit || 'minutes';
      if (!Number.isInteger(every) || every <= 0) {
        return { error: 'Interval value must be a positive integer' };
      }
      if (!['minutes', 'hours'].includes(unit)) {
        return { error: 'Interval unit must be minutes or hours' };
      }
      const minutes = unit === 'hours' ? every * 60 : every;
      if (minutes < 5) {
        return { error: 'Interval must be at least 5 minutes' };
      }
      scheduleConfig = { every, unit };
      collectionIntervalMinutes = minutes;
      break;
    }
    case 'daily':
      scheduleConfig = {
        hour: Number(form.schedule_config.hour),
        minute: Number(form.schedule_config.minute) || 0,
        timezone: form.schedule_config.timezone || 'UTC',
      };
      if (!Number.isInteger(scheduleConfig.hour) ||
          scheduleConfig.hour < 0 || scheduleConfig.hour > 23) {
        return { error: 'Daily hour must be 0-23' };
      }
      break;
    case 'weekly':
      scheduleConfig = {
        day_of_week: Number(form.schedule_config.day_of_week),
        hour: Number(form.schedule_config.hour),
        minute: Number(form.schedule_config.minute) || 0,
        timezone: form.schedule_config.timezone || 'UTC',
      };
      if (![0, 1, 2, 3, 4, 5, 6].includes(scheduleConfig.day_of_week)) {
        return { error: 'Weekly day must be 0 (Sunday) through 6 (Saturday)' };
      }
      if (!Number.isInteger(scheduleConfig.hour) ||
          scheduleConfig.hour < 0 || scheduleConfig.hour > 23) {
        return { error: 'Weekly hour must be 0-23' };
      }
      break;
    default:
      return { error: `Unknown schedule_type: ${form.schedule_type}` };
  }

  // For custom_sql monitors the form's UI only collects
  // source_config.segment_column (the column alias in the user's
  // query). monitor.segment_by must also be set so the rest of the
  // system (engine, dashboard) knows the monitor is segmented —
  // otherwise the frontend treats it as scalar and the trend line
  // zig-zags across per-segment points. Mirror the segment_column
  // value here so the two fields stay consistent.
  const computedSegmentBy =
    form.source_type === 'custom_sql' && form.source_config.segment_column
      ? form.source_config.segment_column
      : form.segment_by || null;

  return {
    payload: {
      name: form.name.trim(),
      display_name: form.display_name || null,
      description: form.description || null,
      is_active: form.is_active,
      source_type: form.source_type,
      source_config: sourceConfig,
      evaluation_mode: form.evaluation_mode,
      evaluation_config: evaluationConfig,
      segment_by: computedSegmentBy,
      collection_interval_minutes: collectionIntervalMinutes,
      min_data_points: minPts,
      schedule_type: form.schedule_type,
      schedule_config: scheduleConfig,
    },
  };
};

// Seed the form state from a saved monitor (edit mode). The DB columns
// store JSONB blobs; spread them into the flat source_config /
// evaluation_config keys our local state uses so each input is
// controlled.
const seedFromMonitor = (m) => {
  const sourceCfg = m.source_config || {};
  const evalCfg = m.evaluation_config || {};
  return {
    name: m.name || '',
    display_name: m.display_name || '',
    description: m.description || '',
    is_active: m.is_active !== false,
    source_type: m.source_type || 'builtin_table_metric',
    source_config: {
      table_name: sourceCfg.table_name || '',
      column_name: sourceCfg.column_name || '',
      metric: sourceCfg.metric || 'row_count',
      query: sourceCfg.query || '',
      value_column: sourceCfg.value_column || '',
      segment_column: sourceCfg.segment_column || '',
      aggregation_method: sourceCfg.aggregation_method || 'sum',
    },
    evaluation_mode: m.evaluation_mode || 'statistical',
    evaluation_config: {
      method: evalCfg.method || 'zscore',
      sensitivity:
        evalCfg.sensitivity !== undefined && evalCfg.sensitivity !== null
          ? Number(evalCfg.sensitivity)
          : 1.0,
      baseline_window_days: evalCfg.baseline_window_days || 14,
      direction: evalCfg.direction || 'below',
      warning:
        evalCfg.warning !== undefined && evalCfg.warning !== null ? evalCfg.warning : '',
      critical:
        evalCfg.critical !== undefined && evalCfg.critical !== null
          ? evalCfg.critical
          : '',
    },
    segment_by: m.segment_by || '',
    collection_interval_minutes: m.collection_interval_minutes || 60,
    min_data_points: m.min_data_points || 3,
    schedule_type: m.schedule_type || 'interval',
    // Merge the saved schedule_config over the defaults so unrelated
    // fields (e.g. hour/minute when type is interval) keep their initial
    // values and don't go undefined-on-edit, which would null-ref the
    // conditional inputs below.
    schedule_config: {
      ...EMPTY_FORM.schedule_config,
      ...(m.schedule_config || {}),
    },
  };
};

const MonitorForm = () => {
  const { connectionSlug, monitorId } = useParams();
  const navigate = useNavigate();
  const { activeConnection, getConnectionBySlug, loading: connectionsLoading } =
    useConnection();
  const { updateBreadcrumbs, showNotification } = useUI();

  const connection = connectionSlug
    ? getConnectionBySlug(connectionSlug)
    : activeConnection;
  const connectionId = connection?.id;
  const isEdit = !!monitorId && monitorId !== 'new';

  // ------------------------------------------------------------------
  // Form state. We keep one flat object so we can seed it from the
  // server in edit mode without juggling N separate useStates.
  // ------------------------------------------------------------------
  const [form, setForm] = useState(EMPTY_FORM);
  const [seeded, setSeeded] = useState(false);
  const [error, setError] = useState(null);

  // Tables + columns dropdowns. Built-in metrics need both; custom SQL
  // doesn't need them but we still load tables so the user can paste a
  // table name into their query confidently.
  const [tables, setTables] = useState([]);
  const [tablesLoading, setTablesLoading] = useState(false);
  const [columns, setColumns] = useState([]);
  const [columnsLoading, setColumnsLoading] = useState(false);

  // Edit-mode: fetch the existing monitor so we can populate the form.
  const {
    data: existing,
    isLoading: existingLoading,
    error: existingError,
  } = useMonitor(connectionId, isEdit ? monitorId : null);

  const createMutation = useCreateMonitor(connectionId);
  const updateMutation = useUpdateMonitor(connectionId, isEdit ? monitorId : null);

  // ------------------------------------------------------------------
  // Breadcrumbs
  // ------------------------------------------------------------------
  useEffect(() => {
    updateBreadcrumbs([
      { name: 'Dashboard', href: '/dashboard' },
      { name: 'Monitors', href: `/monitors/${connectionSlug || ''}` },
      { name: isEdit ? 'Edit Monitor' : 'New Monitor' },
    ]);
  }, [updateBreadcrumbs, connectionSlug, isEdit]);

  // ------------------------------------------------------------------
  // Redirect bare /monitors/new (no slug) to the active connection so
  // the form has a connectionId to work with.
  // ------------------------------------------------------------------
  useEffect(() => {
    if (!connectionSlug && !connectionsLoading && activeConnection?.slug) {
      navigate(
        `/monitors/${activeConnection.slug}/${isEdit ? monitorId : 'new'}`,
        { replace: true }
      );
    }
  }, [connectionSlug, connectionsLoading, activeConnection, isEdit, monitorId, navigate]);

  // ------------------------------------------------------------------
  // Seed form state from server in edit mode. Guarded by `seeded` so
  // user edits aren't blown away when the React Query cache refreshes.
  // ------------------------------------------------------------------
  useEffect(() => {
    if (isEdit && existing && !seeded) {
      setForm(seedFromMonitor(existing));
      setSeeded(true);
    }
  }, [isEdit, existing, seeded]);

  // Reset the seeded flag when the URL switches to a different monitor
  // (e.g., the user opens a second monitor's edit page via deep link).
  useEffect(() => {
    setSeeded(false);
  }, [monitorId]);

  // ------------------------------------------------------------------
  // Load tables when connection is known. Tables don't change often
  // (warehouse metadata is cached) so we don't refetch on every render.
  // ------------------------------------------------------------------
  useEffect(() => {
    if (!connectionId) return undefined;
    let cancelled = false;
    // Clear before fetching so dropdowns don't briefly show the previous
    // connection's table list while the new one loads.
    setTables([]);
    setTablesLoading(true);
    metadataAPI
      .getTableMetadataTiered(connectionId)
      .then((resp) => {
        if (cancelled) return;
        const raw = resp?.tables || resp?.data?.tables || resp || [];
        const names = (Array.isArray(raw) ? raw : [])
          .map((t) => {
            if (typeof t === 'string') return t;
            const name = t.table_name || t.name;
            const schema = t.table_schema || t.schema;
            const db = t.table_catalog || t.database;
            if (name && name.includes('.')) return name;
            if (db && schema && name) return `${db}.${schema}.${name}`;
            if (schema && name) return `${schema}.${name}`;
            return name || '';
          })
          .filter(Boolean);
        setTables(names);
      })
      .catch((e) => {
        if (cancelled) return;
        showNotification(`Failed to load tables: ${e.message}`, 'error');
      })
      .finally(() => {
        if (!cancelled) setTablesLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [connectionId, showNotification]);

  // ------------------------------------------------------------------
  // Load columns whenever the selected table changes (only relevant
  // for column-level built-in metrics, but harmless to load for the
  // segment_by picker on table-level metrics too).
  // ------------------------------------------------------------------
  const selectedTable = form.source_config.table_name;
  useEffect(() => {
    if (!connectionId || !selectedTable) {
      setColumns([]);
      return undefined;
    }
    let cancelled = false;
    // Same reason as tables — clear so the dropdown doesn't flash the
    // previous table's columns until the new query resolves.
    setColumns([]);
    setColumnsLoading(true);
    metadataAPI
      .getTableColumnsTiered(connectionId, selectedTable)
      .then((resp) => {
        if (cancelled) return;
        const raw = resp?.columns || resp?.data?.columns || resp || [];
        const names = (Array.isArray(raw) ? raw : [])
          .map((c) => (typeof c === 'string' ? c : c.column_name || c.name))
          .filter(Boolean);
        setColumns(names);
      })
      .catch((e) => {
        if (cancelled) return;
        showNotification(`Failed to load columns: ${e.message}`, 'error');
      })
      .finally(() => {
        if (!cancelled) setColumnsLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [connectionId, selectedTable, showNotification]);

  // ------------------------------------------------------------------
  // Setters. Two helpers keep the JSX terse without coupling to the
  // shape of `form` everywhere.
  // ------------------------------------------------------------------
  const setField = (name, value) => {
    setForm((prev) => ({ ...prev, [name]: value }));
  };
  const setSource = (name, value) => {
    setForm((prev) => ({
      ...prev,
      source_config: { ...prev.source_config, [name]: value },
    }));
  };
  const setEval = (name, value) => {
    setForm((prev) => ({
      ...prev,
      evaluation_config: { ...prev.evaluation_config, [name]: value },
    }));
  };

  // When the user picks a different source_type, reset `metric` to a
  // valid default for that type — otherwise the field carries over the
  // previous type's metric (e.g., 'row_count' from table-metric) into
  // column-metric, which the backend rejects with
  // "metric must be one of ('null_pct', ...), got 'row_count'".
  // segment_by is preserved across switches because it represents a
  // logical dimension name that's meaningful in either source path.
  // Other fields in source_config (table_name etc.) are also preserved
  // so toggling back doesn't wipe the user's work.
  const defaultMetricFor = (sourceType) => {
    if (sourceType === 'builtin_table_metric') return 'row_count';
    if (sourceType === 'builtin_column_metric') return 'null_pct';
    return ''; // custom_sql doesn't use metric
  };

  const onChangeSourceType = (newType) => {
    setForm((prev) => ({
      ...prev,
      source_type: newType,
      source_config: {
        ...prev.source_config,
        metric: defaultMetricFor(newType),
      },
    }));
  };

  const onChangeEvaluationMode = (newMode) => {
    setField('evaluation_mode', newMode);
  };

  // ------------------------------------------------------------------
  // Submit
  // ------------------------------------------------------------------
  const handleSubmit = async (e) => {
    e.preventDefault();
    setError(null);

    const { payload, error: buildError } = buildPayload(form);
    if (buildError) {
      setError(buildError);
      return;
    }

    try {
      if (isEdit) {
        await updateMutation.mutateAsync(payload);
        showNotification('Monitor updated', 'success');
      } else {
        await createMutation.mutateAsync(payload);
        showNotification('Monitor created', 'success');
      }
      navigate(`/monitors/${connectionSlug}`);
    } catch (err) {
      const msg = err?.message || 'Failed to save monitor';
      setError(msg);
      showNotification(msg, 'error');
    }
  };

  const saving = createMutation.isPending || updateMutation.isPending;

  // Column-level metric currently selected? Used to drive whether the
  // Column dropdown is required + visible.
  const isColumnMetric = form.source_type === 'builtin_column_metric';
  const isCustomSql = form.source_type === 'custom_sql';
  const isStatistical = form.evaluation_mode === 'statistical';
  const isThreshold = form.evaluation_mode === 'threshold';

  // For the segment_by dropdown on built-in metrics: any column from the
  // selected table is a candidate. Custom SQL handles segmentation via
  // source_config.segment_column instead, so segment_by is a free-text
  // input there.
  const segmentByOptions = useMemo(() => columns, [columns]);

  // ------------------------------------------------------------------
  // Render
  // ------------------------------------------------------------------
  if (connectionsLoading) {
    return (
      <div className="max-w-3xl mx-auto px-4 py-12 flex items-center justify-center text-gray-500">
        <ArrowPathIcon className="h-5 w-5 animate-spin mr-2" />
        Loading connection...
      </div>
    );
  }

  if (!connection) {
    return (
      <div className="max-w-3xl mx-auto px-4 py-12 text-center text-sm text-gray-500">
        No connection selected.
      </div>
    );
  }

  if (isEdit && existingLoading) {
    return (
      <div className="max-w-3xl mx-auto px-4 py-12 flex items-center justify-center text-gray-500">
        <ArrowPathIcon className="h-5 w-5 animate-spin mr-2" />
        Loading monitor...
      </div>
    );
  }

  if (isEdit && existingError) {
    return (
      <div className="max-w-3xl mx-auto px-4 py-12 text-center text-sm text-red-600">
        {existingError.message || 'Failed to load monitor'}
      </div>
    );
  }

  return (
    <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
      <div className="flex items-center mb-6">
        <button
          type="button"
          onClick={() => navigate(`/monitors/${connectionSlug || ''}`)}
          className="mr-3 p-2 rounded-full text-gray-400 hover:text-gray-600"
          aria-label="Back to monitors"
        >
          <ArrowLeftIcon className="h-5 w-5" />
        </button>
        <div>
          <h1 className="text-2xl font-semibold text-gray-900">
            {isEdit ? 'Edit Monitor' : 'New Monitor'}
          </h1>
          <p className="text-sm text-gray-500">
            {connection.name}
            {form.source_type && (
              <>
                {' '}
                · {monitorsService.sourceTypeLabel(form.source_type)}
              </>
            )}
          </p>
        </div>
      </div>

      {error && (
        <div className="mb-6 rounded-md bg-red-50 border border-red-200 p-4">
          <div className="flex">
            <ExclamationTriangleIcon className="h-5 w-5 text-red-400 flex-shrink-0" />
            <p className="ml-3 text-sm text-red-700">{error}</p>
          </div>
        </div>
      )}

      <form onSubmit={handleSubmit} className="space-y-8">
        {/* ----------------- Identity ----------------- */}
        <section className="bg-white shadow rounded-lg p-6 space-y-4">
          <h2 className="text-lg font-medium text-gray-900">Basics</h2>

          <div>
            <label className="block text-sm font-medium text-gray-700">
              Name <span className="text-red-500">*</span>
            </label>
            <input
              type="text"
              value={form.name}
              onChange={(e) => setField('name', e.target.value)}
              required
              className="mt-1 block w-full rounded-md border-gray-300 shadow-sm sm:text-sm"
              placeholder="orders.row_count daily"
            />
            <p className="mt-1 text-xs text-gray-500">
              Internal identifier — must be unique within this connection.
            </p>
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700">
              Display name
            </label>
            <input
              type="text"
              value={form.display_name}
              onChange={(e) => setField('display_name', e.target.value)}
              className="mt-1 block w-full rounded-md border-gray-300 shadow-sm sm:text-sm"
              placeholder="Daily order count"
            />
          </div>

          <div>
            <label className="block text-sm font-medium text-gray-700">
              Description
            </label>
            <textarea
              value={form.description}
              onChange={(e) => setField('description', e.target.value)}
              rows={2}
              className="mt-1 block w-full rounded-md border-gray-300 shadow-sm sm:text-sm"
            />
          </div>

          <div className="flex items-center">
            <input
              id="is_active"
              type="checkbox"
              checked={form.is_active}
              onChange={(e) => setField('is_active', e.target.checked)}
              className="h-4 w-4 rounded border-gray-300 text-indigo-600 focus:ring-indigo-500"
            />
            <label htmlFor="is_active" className="ml-2 text-sm text-gray-700">
              Active (uncheck to pause this monitor without deleting it)
            </label>
          </div>
        </section>

        {/* ----------------- Source ----------------- */}
        <section className="bg-white shadow rounded-lg p-6 space-y-4">
          <h2 className="text-lg font-medium text-gray-900">What to measure</h2>

          <div className="grid grid-cols-1 gap-3">
            {SOURCE_OPTIONS.map((opt) => (
              <label
                key={opt.value}
                className={`flex items-start p-3 border rounded-md cursor-pointer ${
                  form.source_type === opt.value
                    ? 'border-indigo-500 bg-indigo-50'
                    : 'border-gray-200 hover:border-gray-300'
                }`}
              >
                <input
                  type="radio"
                  name="source_type"
                  value={opt.value}
                  checked={form.source_type === opt.value}
                  onChange={() => onChangeSourceType(opt.value)}
                  className="mt-0.5 h-4 w-4 text-indigo-600"
                />
                <div className="ml-3">
                  <div className="text-sm font-medium text-gray-900">{opt.label}</div>
                  <div className="text-xs text-gray-500">{opt.blurb}</div>
                </div>
              </label>
            ))}
          </div>

          {/* Built-in (table OR column) shares table picker */}
          {!isCustomSql && (
            <div>
              <label className="block text-sm font-medium text-gray-700">
                Table <span className="text-red-500">*</span>
              </label>
              <select
                value={form.source_config.table_name}
                onChange={(e) => {
                  setSource('table_name', e.target.value);
                  // Wipe column when switching tables — old column likely
                  // doesn't exist on the new one.
                  setSource('column_name', '');
                }}
                disabled={tablesLoading}
                required
                className="mt-1 block w-full rounded-md border-gray-300 shadow-sm sm:text-sm"
              >
                <option value="">{tablesLoading ? 'Loading...' : 'Select a table'}</option>
                {/* Preserve a saved-but-not-listed table for edit mode so
                    the select doesn't silently reset to empty when the
                    backend's table list differs in casing/qualification. */}
                {form.source_config.table_name &&
                  !tables.includes(form.source_config.table_name) && (
                    <option value={form.source_config.table_name}>
                      {form.source_config.table_name} (saved)
                    </option>
                  )}
                {tables.map((t) => (
                  <option key={t} value={t}>
                    {t}
                  </option>
                ))}
              </select>
            </div>
          )}

          {isColumnMetric && (
            <>
              <div>
                <label className="block text-sm font-medium text-gray-700">
                  Column <span className="text-red-500">*</span>
                </label>
                <select
                  value={form.source_config.column_name}
                  onChange={(e) => setSource('column_name', e.target.value)}
                  disabled={columnsLoading || !form.source_config.table_name}
                  required
                  className="mt-1 block w-full rounded-md border-gray-300 shadow-sm sm:text-sm"
                >
                  <option value="">
                    {columnsLoading ? 'Loading...' : 'Select a column'}
                  </option>
                  {form.source_config.column_name &&
                    !columns.includes(form.source_config.column_name) && (
                      <option value={form.source_config.column_name}>
                        {form.source_config.column_name} (saved)
                      </option>
                    )}
                  {columns.map((c) => (
                    <option key={c} value={c}>
                      {c}
                    </option>
                  ))}
                </select>
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700">
                  Metric <span className="text-red-500">*</span>
                </label>
                <select
                  value={form.source_config.metric}
                  onChange={(e) => setSource('metric', e.target.value)}
                  required
                  className="mt-1 block w-full rounded-md border-gray-300 shadow-sm sm:text-sm"
                >
                  {COLUMN_METRICS.map((m) => (
                    <option key={m.value} value={m.value}>
                      {m.label}
                    </option>
                  ))}
                </select>
              </div>
            </>
          )}

          {isCustomSql && (
            <>
              {/* Plain-language explainer up top — the column-alias mechanic
                  was confusing on first contact when it was only mentioned
                  via the field labels. Worked example shows the contract:
                  whatever you alias as 'value' becomes the metric, whatever
                  you alias as 'segment' becomes the dimension. */}
              <div className="rounded-md bg-blue-50 border border-blue-200 p-3 text-xs text-blue-900">
                <p className="font-medium">How custom SQL works</p>
                <p className="mt-1">
                  Write a query that returns one row per data point. Alias the
                  numeric column as <code className="font-mono">value</code>; for
                  segmented monitors, also alias the dimension column as{' '}
                  <code className="font-mono">segment</code>. You can pick
                  different aliases below if you prefer.
                </p>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700">
                  SQL query <span className="text-red-500">*</span>
                </label>
                <textarea
                  value={form.source_config.query}
                  onChange={(e) => setSource('query', e.target.value)}
                  rows={7}
                  required
                  className="mt-1 block w-full rounded-md border-gray-300 shadow-sm font-mono text-xs"
                  placeholder={`-- Segmented example: row count per region, last day\nSELECT\n  region   AS segment,\n  COUNT(*) AS value\nFROM orders\nWHERE created_at >= DATEADD(day, -1, CURRENT_TIMESTAMP())\nGROUP BY region`}
                />
                <p className="mt-1 text-xs text-gray-500">
                  For a scalar (non-segmented) monitor, return a single row with
                  just the value column — no GROUP BY needed.
                </p>
              </div>
              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700">
                    Value column <span className="text-red-500">*</span>
                  </label>
                  <input
                    type="text"
                    value={form.source_config.value_column}
                    onChange={(e) => setSource('value_column', e.target.value)}
                    required
                    className="mt-1 block w-full rounded-md border-gray-300 shadow-sm sm:text-sm"
                    placeholder="value"
                  />
                  <p className="mt-1 text-xs text-gray-500">
                    Name of the metric column in your query (e.g.{' '}
                    <code className="font-mono">value</code> in the example above).
                  </p>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700">
                    Segment column
                  </label>
                  <input
                    type="text"
                    value={form.source_config.segment_column}
                    onChange={(e) => setSource('segment_column', e.target.value)}
                    className="mt-1 block w-full rounded-md border-gray-300 shadow-sm sm:text-sm"
                    placeholder="segment"
                  />
                  <p className="mt-1 text-xs text-gray-500">
                    Leave blank for scalar monitors. For segmented monitors, use
                    the dimension column name (e.g.{' '}
                    <code className="font-mono">segment</code>).
                  </p>
                </div>
              </div>

              {/* Aggregation method picker — only relevant when the
                  monitor is segmented (otherwise there's nothing to
                  collapse across segments). Drives the "overall" value
                  shown on the collapsed card and in the trend chart.
                  Built-in metrics ignore this — the engine computes a
                  true un-grouped value via a separate query. */}
              {form.source_config.segment_column && (
                <div>
                  <label className="block text-sm font-medium text-gray-700">
                    Aggregation across segments
                  </label>
                  <select
                    value={form.source_config.aggregation_method || 'sum'}
                    onChange={(e) =>
                      setSource('aggregation_method', e.target.value)
                    }
                    className="mt-1 block w-full rounded-md border-gray-300 shadow-sm sm:text-sm"
                  >
                    <option value="sum">Sum — total across segments</option>
                    <option value="avg">Avg — mean of segment values</option>
                    <option value="min">Min — smallest segment value</option>
                    <option value="max">Max — largest segment value</option>
                  </select>
                  <p className="mt-1 text-xs text-gray-500">
                    How the dashboard collapses per-segment results into one
                    overall number. Default is sum for SUM/COUNT-style
                    queries. The detail modal shows all four aggregations
                    side by side regardless.
                  </p>
                </div>
              )}
            </>
          )}

          {/* Segment-by: only meaningful for built-in monitors (custom_sql
              uses source_config.segment_column instead). */}
          {!isCustomSql && (
            <div>
              <label className="block text-sm font-medium text-gray-700">
                Segment by (optional)
              </label>
              <select
                value={form.segment_by}
                onChange={(e) => setField('segment_by', e.target.value)}
                disabled={columnsLoading || !form.source_config.table_name}
                className="mt-1 block w-full rounded-md border-gray-300 shadow-sm sm:text-sm"
              >
                <option value="">(none — single scalar)</option>
                {segmentByOptions.map((c) => (
                  <option key={c} value={c}>
                    {c}
                  </option>
                ))}
              </select>
              <p className="mt-1 text-xs text-gray-500">
                Track this metric per dimension value (e.g. per region). Each segment
                gets its own baseline + alert path.
              </p>
            </div>
          )}
        </section>

        {/* ----------------- Evaluation ----------------- */}
        <section className="bg-white shadow rounded-lg p-6 space-y-4">
          <h2 className="text-lg font-medium text-gray-900">How to alert</h2>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-3">
            {EVALUATION_OPTIONS.map((opt) => (
              <label
                key={opt.value}
                className={`flex items-start p-3 border rounded-md cursor-pointer ${
                  form.evaluation_mode === opt.value
                    ? 'border-indigo-500 bg-indigo-50'
                    : 'border-gray-200 hover:border-gray-300'
                }`}
              >
                <input
                  type="radio"
                  name="evaluation_mode"
                  value={opt.value}
                  checked={form.evaluation_mode === opt.value}
                  onChange={() => onChangeEvaluationMode(opt.value)}
                  className="mt-0.5 h-4 w-4 text-indigo-600"
                />
                <div className="ml-3">
                  <div className="text-sm font-medium text-gray-900">{opt.label}</div>
                  <div className="text-xs text-gray-500">{opt.blurb}</div>
                </div>
              </label>
            ))}
          </div>

          {isStatistical && (
            <>
              <div>
                <label className="block text-sm font-medium text-gray-700">
                  Detection method
                </label>
                <select
                  value={form.evaluation_config.method}
                  onChange={(e) => setEval('method', e.target.value)}
                  className="mt-1 block w-full rounded-md border-gray-300 shadow-sm sm:text-sm"
                >
                  {STATISTICAL_METHODS.map((m) => (
                    <option key={m.value} value={m.value}>
                      {m.label}
                    </option>
                  ))}
                </select>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700">
                  Sensitivity
                </label>
                <div className="mt-1 flex items-center">
                  <input
                    type="range"
                    min="0.5"
                    max="2"
                    step="0.1"
                    value={form.evaluation_config.sensitivity}
                    onChange={(e) =>
                      setEval('sensitivity', parseFloat(e.target.value))
                    }
                    className="w-full"
                  />
                  <span className="ml-3 w-10 text-sm text-gray-700">
                    {Number(form.evaluation_config.sensitivity).toFixed(1)}
                  </span>
                </div>
                <p className="mt-1 text-xs text-gray-500">
                  Higher = more sensitive (fires on smaller deviations).
                </p>
              </div>

              <div>
                <label className="block text-sm font-medium text-gray-700">
                  Baseline window (days)
                </label>
                <input
                  type="number"
                  min="1"
                  max="90"
                  value={form.evaluation_config.baseline_window_days}
                  onChange={(e) =>
                    setEval(
                      'baseline_window_days',
                      Number.isNaN(parseInt(e.target.value, 10))
                        ? ''
                        : parseInt(e.target.value, 10)
                    )
                  }
                  className="mt-1 block w-full rounded-md border-gray-300 shadow-sm sm:text-sm"
                />
              </div>
            </>
          )}

          {isThreshold && (
            <>
              <div>
                <label className="block text-sm font-medium text-gray-700">
                  Direction
                </label>
                <select
                  value={form.evaluation_config.direction}
                  onChange={(e) => setEval('direction', e.target.value)}
                  className="mt-1 block w-full rounded-md border-gray-300 shadow-sm sm:text-sm"
                >
                  <option value="below">Alert when value goes below</option>
                  <option value="above">Alert when value goes above</option>
                </select>
              </div>

              <div className="grid grid-cols-2 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700">
                    Warning threshold
                  </label>
                  <input
                    type="number"
                    step="any"
                    value={form.evaluation_config.warning ?? ''}
                    onChange={(e) =>
                      setEval(
                        'warning',
                        e.target.value === '' ? '' : Number(e.target.value)
                      )
                    }
                    className="mt-1 block w-full rounded-md border-gray-300 shadow-sm sm:text-sm"
                    placeholder="(optional)"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700">
                    Critical threshold
                  </label>
                  <input
                    type="number"
                    step="any"
                    value={form.evaluation_config.critical ?? ''}
                    onChange={(e) =>
                      setEval(
                        'critical',
                        e.target.value === '' ? '' : Number(e.target.value)
                      )
                    }
                    className="mt-1 block w-full rounded-md border-gray-300 shadow-sm sm:text-sm"
                    placeholder="(optional)"
                  />
                </div>
              </div>
              <p className="text-xs text-gray-500">
                Set at least one. For direction=below, Critical must be ≤ Warning;
                for direction=above, Critical must be ≥ Warning.
              </p>
            </>
          )}
        </section>

        {/* ----------------- Schedule ----------------- */}
        <section className="bg-white shadow rounded-lg p-6 space-y-4">
          <h2 className="text-lg font-medium text-gray-900">Schedule</h2>

          {/* Top-level toggle: run on a schedule, or manual-only.
              Unchecking switches schedule_type to 'manual' and stashes
              the previous type in case the user re-checks (so they
              don't lose their "every 60 minutes" config by toggling
              the checkbox). */}
          <div className="flex items-center">
            <input
              id="schedule_enabled"
              type="checkbox"
              checked={form.schedule_type !== 'manual'}
              onChange={(e) => {
                if (e.target.checked) {
                  // Re-enable: default to interval if no prior type stashed.
                  setField('schedule_type', 'interval');
                } else {
                  setField('schedule_type', 'manual');
                }
              }}
              className="h-4 w-4 rounded border-gray-300 text-indigo-600"
            />
            <label htmlFor="schedule_enabled" className="ml-2 text-sm text-gray-700">
              Run automatically on a schedule
            </label>
          </div>
          {form.schedule_type === 'manual' && (
            <p className="text-xs text-gray-500 -mt-2">
              Automatic scheduling is off. The monitor only runs when you
              click Run Now.
            </p>
          )}

          {/* Cadence radio cards — visible only when schedule is enabled. */}
          {form.schedule_type !== 'manual' && (
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-3">
              {[
                { value: 'interval', label: 'Interval',
                  blurb: 'Run every N minutes or hours.' },
                { value: 'daily', label: 'Daily',
                  blurb: 'Run once a day at a specific time.' },
                { value: 'weekly', label: 'Weekly',
                  blurb: 'Run once a week on a chosen day + time.' },
              ].map((opt) => (
                <label
                  key={opt.value}
                  className={`p-3 border rounded-md cursor-pointer ${
                    form.schedule_type === opt.value
                      ? 'border-indigo-500 bg-indigo-50'
                      : 'border-gray-200 hover:border-gray-300'
                  }`}
                >
                  <div className="flex items-start">
                    <input
                      type="radio"
                      name="schedule_type"
                      value={opt.value}
                      checked={form.schedule_type === opt.value}
                      onChange={() => setField('schedule_type', opt.value)}
                      className="mt-0.5 h-4 w-4 text-indigo-600"
                    />
                    <div className="ml-3">
                      <div className="text-sm font-medium text-gray-900">
                        {opt.label}
                      </div>
                      <div className="text-xs text-gray-500">{opt.blurb}</div>
                    </div>
                  </div>
                </label>
              ))}
            </div>
          )}

          {/* Cadence-specific fields. Each branch sets ONLY the
              schedule_config keys that apply to it; buildPayload strips
              everything else at submit time so the backend dataclass
              parser doesn't see stale fields from a previous selection. */}
          {form.schedule_type === 'interval' && (
            <div className="grid grid-cols-2 gap-4">
              <div>
                <label className="block text-sm font-medium text-gray-700">
                  Every
                </label>
                <input
                  type="number"
                  min="1"
                  value={form.schedule_config.every}
                  onChange={(e) => {
                    const v = parseInt(e.target.value, 10);
                    setForm((prev) => ({
                      ...prev,
                      schedule_config: {
                        ...prev.schedule_config,
                        every: Number.isNaN(v) ? '' : v,
                      },
                    }));
                  }}
                  className="mt-1 block w-full rounded-md border-gray-300 shadow-sm sm:text-sm"
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-gray-700">
                  Unit
                </label>
                <select
                  value={form.schedule_config.unit || 'minutes'}
                  onChange={(e) => {
                    setForm((prev) => ({
                      ...prev,
                      schedule_config: {
                        ...prev.schedule_config,
                        unit: e.target.value,
                      },
                    }));
                  }}
                  className="mt-1 block w-full rounded-md border-gray-300 shadow-sm sm:text-sm"
                >
                  <option value="minutes">Minutes</option>
                  <option value="hours">Hours</option>
                </select>
              </div>
              <p className="col-span-2 text-xs text-gray-500">
                Minimum is 5 minutes total (so "every 0 hours" or "every 1
                minute" are rejected).
              </p>
            </div>
          )}

          {(form.schedule_type === 'daily' ||
            form.schedule_type === 'weekly') && (
            <div className="space-y-4">
              {form.schedule_type === 'weekly' && (
                <div>
                  <label className="block text-sm font-medium text-gray-700">
                    Day of week
                  </label>
                  <select
                    value={form.schedule_config.day_of_week ?? 1}
                    onChange={(e) => {
                      setForm((prev) => ({
                        ...prev,
                        schedule_config: {
                          ...prev.schedule_config,
                          day_of_week: parseInt(e.target.value, 10),
                        },
                      }));
                    }}
                    className="mt-1 block w-full rounded-md border-gray-300 shadow-sm sm:text-sm"
                  >
                    {DAY_OF_WEEK_LABELS.map((d) => (
                      <option key={d.value} value={d.value}>{d.label}</option>
                    ))}
                  </select>
                </div>
              )}
              <div className="grid grid-cols-3 gap-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700">
                    Hour (0-23)
                  </label>
                  <input
                    type="number"
                    min="0"
                    max="23"
                    value={form.schedule_config.hour ?? 9}
                    onChange={(e) => {
                      const v = parseInt(e.target.value, 10);
                      setForm((prev) => ({
                        ...prev,
                        schedule_config: {
                          ...prev.schedule_config,
                          hour: Number.isNaN(v) ? '' : v,
                        },
                      }));
                    }}
                    className="mt-1 block w-full rounded-md border-gray-300 shadow-sm sm:text-sm"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700">
                    Minute (0-59)
                  </label>
                  <input
                    type="number"
                    min="0"
                    max="59"
                    value={form.schedule_config.minute ?? 0}
                    onChange={(e) => {
                      const v = parseInt(e.target.value, 10);
                      setForm((prev) => ({
                        ...prev,
                        schedule_config: {
                          ...prev.schedule_config,
                          minute: Number.isNaN(v) ? '' : v,
                        },
                      }));
                    }}
                    className="mt-1 block w-full rounded-md border-gray-300 shadow-sm sm:text-sm"
                  />
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700">
                    Timezone
                  </label>
                  <select
                    value={form.schedule_config.timezone || 'UTC'}
                    onChange={(e) => {
                      setForm((prev) => ({
                        ...prev,
                        schedule_config: {
                          ...prev.schedule_config,
                          timezone: e.target.value,
                        },
                      }));
                    }}
                    className="mt-1 block w-full rounded-md border-gray-300 shadow-sm sm:text-sm"
                  >
                    {/* Include the user's browser TZ if it's not in the
                        common list — otherwise editing a monitor saved
                        with a less-common TZ would silently re-default. */}
                    {(() => {
                      const tzs = new Set(COMMON_TIMEZONES);
                      if (form.schedule_config.timezone) {
                        tzs.add(form.schedule_config.timezone);
                      }
                      return [...tzs].sort().map((tz) => (
                        <option key={tz} value={tz}>{tz}</option>
                      ));
                    })()}
                  </select>
                </div>
              </div>
            </div>
          )}

          <div className="border-t border-gray-200 pt-4">
            <label className="block text-sm font-medium text-gray-700">
              Minimum data points
            </label>
            <input
              type="number"
              min="1"
              value={form.min_data_points}
              onChange={(e) =>
                setField(
                  'min_data_points',
                  Number.isNaN(parseInt(e.target.value, 10))
                    ? ''
                    : parseInt(e.target.value, 10)
                )
              }
              className="mt-1 block w-full max-w-xs rounded-md border-gray-300 shadow-sm sm:text-sm"
            />
            <p className="mt-1 text-xs text-gray-500">
              Statistical detection waits until this many points are collected.
            </p>
          </div>
        </section>

        {/* ----------------- Actions ----------------- */}
        <div className="flex justify-end space-x-3">
          <button
            type="button"
            onClick={() => navigate(`/monitors/${connectionSlug || ''}`)}
            className="inline-flex items-center px-4 py-2 border border-gray-300 shadow-sm text-sm font-medium rounded-md text-gray-700 bg-white hover:bg-gray-50"
          >
            Cancel
          </button>
          <button
            type="submit"
            disabled={saving}
            className="inline-flex items-center px-4 py-2 border border-transparent shadow-sm text-sm font-medium rounded-md text-white bg-indigo-600 hover:bg-indigo-700 disabled:opacity-50"
          >
            {saving ? (
              <>
                <ArrowPathIcon className="h-4 w-4 mr-2 animate-spin" />
                Saving...
              </>
            ) : (
              <>
                <CheckIcon className="h-4 w-4 mr-2" />
                {isEdit ? 'Save changes' : 'Create monitor'}
              </>
            )}
          </button>
        </div>
      </form>
    </div>
  );
};

export default MonitorForm;
